import React, { useState } from 'react';
import debounce from 'lodash.debounce';

import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    FormFeedback,
} from 'reactstrap';

import { validation as validationFunctions } from '../utils';
import { capitalize, maskEmail } from '../utils/helpers';
import { defaultFormData } from '../utils/constants/general';
import { checkEmailAvailability, sendEmailLink } from '../utils/firebase/auth';
import { checkUsernameAvailability } from '../utils/firebase/firestore';

function ReservationForm({ isModalContent = false, className = '', submitButtonClassName = '', showAlert = () => null, closeModal = () => null, showLoginForm = () => null }) {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({ ...defaultFormData });
  const idPrefix = isModalContent ? 'modal' : 'main';
  const usernameInputId = `${idPrefix}-signup-username`;
  const usernameFeedbackId = `${usernameInputId}-feedback`;
  const emailInputId = `${idPrefix}-signup-email`;
  const emailFeedbackId = `${emailInputId}-feedback`;

  const {
    username: { value: usernameValue, isValid: isUsernameValid, isTouched: isUsernameTouched, message: usernameErrorMessage },
    email: { value: emailValue, isValid: isEmailValid, isTouched: isEmailTouched, message: emailErrorMessage }
  } = formData;

  const isFormValid = isUsernameValid && isEmailValid;

  const sendSignInEmail = debounce(async () => {
    let alertMessage = 'Something went wrong. Try again.';

    try {
      const { username: { value: usernameValue }, email: { value: emailValue }} = formData;

      setIsLoading(true);

      const isUsernameAvailable = await checkUsernameAvailability(usernameValue);
      const isEmailAvailable = await checkEmailAvailability(emailValue);
      
      if (isUsernameAvailable && isEmailAvailable) {
        await sendEmailLink(emailValue, usernameValue)
        alertMessage = `${usernameValue}, a verification link has been sent to ${maskEmail(emailValue)}.\nCheck your inbox and click the link to finish reserving your username.`;
      }
    } catch ({ code = '', cause, message = '' }) {
      if (code === 'auth/email-already-in-use' || cause?.code === 'custom/email-already-in-use') {
        alertMessage = 'Email address is already in use. Use another email or sign in.';
      } else if (cause?.code === 'custom/username-already-in-use') {
        alertMessage = 'Username is already in use. Try a different username.';
      } else {
        alertMessage = message || 'There was an error claiming your username. Try again soon. If it keeps happening, contact support@spitfacebattles.com';
      }
    } finally {
      setIsLoading(false);
      closeModal();
      showAlert(alertMessage, false);
    }
  }, 300);

  const handleValidation = (name, value) => {
    const validation = validationFunctions[`validate${capitalize(name)}`](value);
    return { ...formData[name], ...validation, isTouched: formData[name].isTouched };
  }; 

  const handleChange = ({ target: { name, value }}) => {
    const validation = handleValidation(name, value);
    setFormData({
      ...formData,
      [name]: {
        ...formData[name],
        ...validation,
        value
      }});
  };

  const handleBur = async ({ target: {name, value }}) => {
    let validation = handleValidation(name, value);

    try {
      if (name === 'username') {
        // setIsLoading(true);
        await checkUsernameAvailability(value);
      }
      
      if (name === 'email') {
        // setIsLoading(true);
        await checkEmailAvailability(value);
      } 
    } catch ({ code = '', cause, message: errorMessage }) {
      let message = errorMessage;
      if (code === 'auth/email-already-in-use' || cause?.code === 'auth/email-already-in-use') {
        message = 'Email address is already in use. Use another email or sign in.';
        validation = { ...validation, message, isValid: false };
      } else if (cause?.code === 'custom/username-already-in-use') {
        message = 'Username is already in use. Try a different username.';
        validation = { ...validation, message, isValid: false };
      }
    }finally {
      // setIsLoading(false);

      setFormData({
        ...formData,
        [name]: {
          ...formData[name],
          ...validation,
          isTouched: true
        }});
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const formFields = ['username', 'email'];
    const isFormValid = formFields.filter(name => !handleValidation(name, formData[name].value));

    if (isFormValid) {
      await sendSignInEmail();
      setFormData({ ...defaultFormData });
    }
  };

  return (
    <Form 
      noValidate
      id="signup-form"
      onSubmit={handleSubmit}
      className={`form-shell d-flex flex-column justify-content-around gap-3 ${className}`}
    >
      <FormGroup floating className="permanent-marker-regular">
        <Input
          required
          type="text"
          name="username"
          id={usernameInputId}
          placeholder="Username (3-20 chars)"
          value={usernameValue}
          invalid={isUsernameTouched && !isUsernameValid}
          aria-required="true"
          aria-invalid={isUsernameTouched && !isUsernameValid}
          aria-describedby={usernameFeedbackId}
          tabIndex={1}
          onBlur={handleBur}
          onChange={handleChange}
          className="input-tracked text-light bg-dark rounded-2 surface-elevated"
        />
        <Label for={usernameInputId} className="text-secondary beta-form-label fw-semibold">Username</Label>
        <FormFeedback id={usernameFeedbackId} aria-live="polite" className={`small mt-1 d-block ${isUsernameTouched && !isUsernameValid ? 'd-initial' : 'd-none'}`}>{usernameErrorMessage}</FormFeedback>
      </FormGroup>

      <FormGroup floating className="no-fricken-bottom-margin permanent-marker-regular">
        <Input
          required
          type="email"
          name="email"
          id={emailInputId}
          placeholder="name@email.com"
          value={emailValue}
          invalid={isEmailTouched && !isEmailValid}
          aria-required="true"
          aria-invalid={isEmailTouched && !isEmailValid}
          aria-describedby={emailFeedbackId}
          tabIndex={2}
          onBlur={handleBur}
          onChange={handleChange}
          className="input-tracked text-light bg-dark rounded-2 surface-elevated"
        />
        <Label for={emailInputId} className="text-secondary beta-form-label fw-semibold">Email Address</Label>
        <FormFeedback id={emailFeedbackId} aria-live="polite" className={`small mt-1 d-block ${isEmailTouched && !isEmailValid ? 'd-initial' : 'd-none'}`}>{emailErrorMessage}</FormFeedback>
      </FormGroup>

      {isModalContent === true && (
        <Button
          type="button"
          color="link"
          tabIndex={3}
          onClick={showLoginForm}
          className="align-items-center fw-medium text-secondary text-decoration-none anton-regular shadow-none align-self-center"
        >
          Already claimed your username?
        </Button>
      )}

      <Button
        id={`${idPrefix}-signup-submit`}
        type='submit'
        color='primary'
        size="sm"
        tabIndex={4}
        aria-busy={isLoading}
        aria-live="polite"
        disabled={isLoading || !isFormValid}
        className={`btn-cta align-self-end fw-semibold ${submitButtonClassName}`}
      >
        {isLoading ? 'Reserving...' : 'Reserve Username'}
      </Button>
    </Form>
  );
}

export default ReservationForm;
